import { createFileRoute } from "@tanstack/react-router"
import {
  CircleHelp,
  FileDown,
  FilePlus2,
  FileText,
  History,
  ListChecks,
  Lock,
  Sparkles,
  UserX,
} from "lucide-react"

import { TRIAL_DAYS } from "@/brand/plan-numbers"
import { breadcrumbJsonLd, buildHead } from "@/core/seo"

import { AnswerBox, Breadcrumb, CtaBand, FaqAccordion, SourceLine } from "../../components/landing"
import { InvoiceGenerator } from "../../components/landing/invoice-generator"
import { HeroBand, SectionTitle, StatRow } from "../../components/landing/page-parts"
import { englishDateToZh } from "../../content/zh/dates"
import { ATO_TAX_INVOICES_URL, CHECKED, REVIEWED } from "../invoice-generator"

/**
 * Chinese version of /invoice-generator (D-WEB-LANG-SCOPE, T1322); the English route carries the
 * hreflang pair too. Only the page and the form are Chinese: the invoice the tool builds stays an
 * English Tax Invoice (ATO wording). Dates and the ATO source are the English page's.
 */
const checked = englishDateToZh(CHECKED)
const reviewed = englishDateToZh(REVIEWED)

export const invoiceGeneratorZhSeo = () =>
  buildHead({
    title: "免费发票生成器（澳洲个体经营者）",
    description:
      "生成 PDF 发票或 Tax Invoice，GST 自动算好，无需账户。发票内容按 ATO 列出的 Tax Invoice 要求，生成的发票为英文。",
    path: "/invoice-generator",
    localePrefix: "zh",
    jsonLd: [breadcrumbJsonLd([{ name: "首页", path: "/zh" }, { name: "发票生成器" }])],
  })

export const Route = createFileRoute("/_brand-layout/zh/invoice-generator")({
  head: () => {
    const { meta, links, scripts } = invoiceGeneratorZhSeo()
    return { meta, links, scripts }
  },
  component: InvoiceGeneratorZhPage,
})

const STATS = [
  { icon: FileDown, value: "PDF", label: "可下载或打印" },
  {
    icon: FileText,
    tone: "success",
    value: "Tax Invoice",
    label: "未登记 GST 则开普通发票（Invoice）",
  },
  { icon: UserX, value: "无需账户", label: "直接使用" },
  { icon: Lock, tone: "success", value: "不会发送给我们", label: "在你的浏览器里运行" },
] as const

/** Same order and count as the English list (a test compares the count). */
const TAX_INVOICE_DETAILS_ZH: readonly string[] = [
  "“Tax Invoice”这几个字",
  "卖方（你）的姓名和 ABN",
  "发票日期",
  "所售商品或服务的说明，以及数量和价格",
  "每笔销售中应税（须缴 GST）的部分",
  "GST 金额，或一句说明总价已含 GST",
  "买方的姓名或 ABN（销售额达到 A$1,000 或以上时）",
]

const FAQ = [
  {
    question: "这个生成器真的免费吗？",
    answer: "是的。不用账户就能生成并下载发票。",
  },
  {
    question: "我没有登记 GST，会怎样？",
    answer: "工具不会加入 GST，生成的文件标题是 Invoice（普通发票）。",
  },
  {
    question: "我填的数字存在哪里？",
    answer: "哪里都不存。数字只留在这个页面的浏览器里，关闭或刷新页面就没了。我们收不到。",
  },
]

export function InvoiceGeneratorZhPage() {
  const source = (
    <SourceLine
      sources={[{ label: "ATO，Tax Invoice", href: ATO_TAX_INVOICES_URL }]}
      checked={checked}
    />
  )
  return (
    <>
      <Breadcrumb items={[{ label: "首页", href: "/" }, { label: "发票生成器" }]} />
      <HeroBand>
        <div className="text-muted-foreground flex flex-wrap items-center gap-3 text-sm">
          <span className="bg-success-soft text-success inline-flex h-6 items-center gap-1.5 rounded-full px-2.5 text-xs font-medium">
            <Sparkles className="size-3.5" aria-hidden="true" />
            免费工具
          </span>
          <span className="inline-flex items-center gap-1.5">
            <History className="size-3.5" aria-hidden="true" />
            最近复核 {reviewed}
          </span>
        </div>
        <h1 className="text-3xl font-bold tracking-tight md:text-5xl">
          免费发票生成器（澳洲个体经营者）
        </h1>
        <div className="max-w-[70ch]">
          <AnswerBox source={source}>
            填写你的信息和项目，工具就会生成 PDF 发票。已登记 GST 的，生成显示 GST 的 Tax
            Invoice；没有登记的，生成普通发票。内容按 ATO 列出的 Tax Invoice
            要求。生成的发票是英文的 Tax Invoice，页面和表单是中文。
          </AnswerBox>
        </div>
        <StatRow label="你能得到什么" stats={STATS} />
      </HeroBand>

      <section className="px-band grid gap-4 py-8" aria-labelledby="make-title">
        <SectionTitle id="make-title" icon={FilePlus2} tone="primary">
          生成发票
        </SectionTitle>
        <InvoiceGenerator />
      </section>

      <section className="px-band grid gap-3 py-8" aria-labelledby="needs-title">
        <SectionTitle id="needs-title" icon={ListChecks} tone="success">
          Tax Invoice 需要什么
        </SectionTitle>
        <ul
          aria-label="Tax Invoice 需要写明的内容"
          className="grid max-w-[70ch] list-disc gap-1.5 pl-5"
        >
          {TAX_INVOICE_DETAILS_ZH.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
        {source}
        <p className="text-muted-foreground max-w-[70ch] text-sm">
          仅为一般性信息，不是税务建议；请向 ATO 或注册税务代理核实你的具体情况。
        </p>
      </section>

      <section className="px-band grid gap-3 py-8" aria-labelledby="faq-title">
        <SectionTitle id="faq-title" icon={CircleHelp} tone="success">
          常见问题
        </SectionTitle>
        <FaqAccordion items={FAQ} />
      </section>

      <section
        aria-labelledby="app-title"
        className="bg-accent text-accent-foreground mx-band my-4 grid gap-1 rounded-lg px-5 py-4"
      >
        <h2 id="app-title" className="font-semibold">
          想要自动编号、发邮件和跟踪收款？
        </h2>
        <p>把每张发票和你的行程、收据放在同一个地方。有免费版。</p>
      </section>

      <CtaBand
        title="发出发票、跟进、收款"
        description={`免费试用 ${TRIAL_DAYS} 天，无需绑定银行卡。`}
        ctaLabel="开始免费试用"
        ctaHref="/login"
      />
    </>
  )
}
